import { CompactCourseModel } from '../models/CourseModels';
import { AVAILABLE_STATES } from '../config/courses';

// Cache for loaded state data
const stateCache: Record<string, CompactCourseModel[]> = {};
//let allCoursesCache: CompactCourse[] | null = null;

// Get cache version from environment, fallback to v1 if not set
const CACHE_VERSION = import.meta.env.VITE_COURSE_CACHE_VERSION || 'v1';
const CACHE_NAME = `fg2p-courses-${CACHE_VERSION}`;

// Import all course files
const courseFiles = import.meta.glob('/courses/*.json', { eager: false });

// Track which states are loaded
const loadedStates = new Set<string>();

// Calculate distance between two points in kilometers
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Load courses for a specific state
async function loadStateData(countryAbbrStateName: string): Promise<CompactCourseModel[]> {
  // Check in-memory cache first
  if (stateCache[countryAbbrStateName]) {
    console.log(`Using cached data for ${countryAbbrStateName}`);
    return stateCache[countryAbbrStateName];
  }

  const cacheKey = `/courses/${countryAbbrStateName}.json`;
  console.log(`Attempting to load courses for ${countryAbbrStateName} from ${cacheKey}`);

  try {
    // Try to get from Cache API first
    const cache = await caches.open(CACHE_NAME);
    const cachedResponse = await cache.match(cacheKey);

    if (cachedResponse) {
      console.log(`Found cached response for ${countryAbbrStateName}`);
      const data = await cachedResponse.json();
      stateCache[countryAbbrStateName] = data[countryAbbrStateName];
      loadedStates.add(countryAbbrStateName);
      return stateCache[countryAbbrStateName];
    }

    // If not in cache, try to get from Vite's imported files
    const courseFile = courseFiles[cacheKey];
    if (courseFile) {
      console.log(`Found course file in Vite's build for ${countryAbbrStateName}`);
      const data = await courseFile() as { [key: string]: CompactCourseModel[] };
      stateCache[countryAbbrStateName] = data[countryAbbrStateName];
      loadedStates.add(countryAbbrStateName);
      return stateCache[countryAbbrStateName];
    }

    // If not in Vite's build, fetch from network
    console.log(`Fetching ${cacheKey} from network`);
    const response = await fetch(cacheKey);
    if (!response.ok) {
      console.error(`Failed to load courses for ${countryAbbrStateName}: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to load courses for ${countryAbbrStateName}: ${response.status} ${response.statusText}`);
    }

    // Clone the response before caching because response body can only be used once
    await cache.put(cacheKey, response.clone());

    const data = await response.json();
    if (!data[countryAbbrStateName]) {
      console.error(`No data found for ${countryAbbrStateName} in response`);
      throw new Error(`No data found for ${countryAbbrStateName} in response`);
    }

    console.log(`Successfully loaded ${data[countryAbbrStateName].length} courses for ${countryAbbrStateName}`);
    stateCache[countryAbbrStateName] = data[countryAbbrStateName];
    loadedStates.add(countryAbbrStateName);
    return stateCache[countryAbbrStateName];
  } catch (error) {
    console.error(`Error loading courses for ${countryAbbrStateName}:`, error);
    return [];
  }
}

// Load Iowa courses immediately
export async function loadIowaCourses(): Promise<void> {
  const iowaState = 'usa_iowa';
  if (!loadedStates.has(iowaState)) {
    await loadStateData(iowaState);
  }
}

// Load remaining states in the background
export async function loadRemainingStates(): Promise<void> {
  const remainingStates = AVAILABLE_STATES.filter(state => !loadedStates.has(state));

  // Load states in chunks to avoid overwhelming the browser
  const CHUNK_SIZE = 3;
  for (let i = 0; i < remainingStates.length; i += CHUNK_SIZE) {
    const chunk = remainingStates.slice(i, i + CHUNK_SIZE);
    await Promise.all(chunk.map(state => loadStateData(state)));
  }
}

// Find courses by name
export async function findCoursesByName(
  searchTerm: string,
  excludePrivate = false
): Promise<CompactCourseModel[]> {
  const normalizedSearch = searchTerm.toLowerCase().trim();
  if (!normalizedSearch) return [];

  // Get all loaded courses
  const loadedCourses = Object.values(stateCache).flat();

  // If we have all states loaded, use them
  if (loadedStates.size === AVAILABLE_STATES.length) {
    return filterAndSortCourses(loadedCourses, normalizedSearch, excludePrivate);
  }

  // Otherwise, load the remaining states
  await loadRemainingStates();
  return filterAndSortCourses(Object.values(stateCache).flat(), normalizedSearch, excludePrivate);
}

// Helper function to filter and sort courses
function filterAndSortCourses(
  courses: CompactCourseModel[],
  searchTerm: string,
  excludePrivate: boolean
): CompactCourseModel[] {
  const results = courses
    .filter(course => {
      if (excludePrivate && course.t === 'private') {
        return false;
      }
      return course.n.toLowerCase().includes(searchTerm) || // Search by name
        course.c.toLowerCase().includes(searchTerm);   // Search by city
    });

  // Group results by match type (name matches first, then city matches)
  const nameMatches = results.filter(course =>
    course.n.toLowerCase().includes(searchTerm)
  );
  const cityMatches = results.filter(course =>
    !course.n.toLowerCase().includes(searchTerm) &&
    course.c.toLowerCase().includes(searchTerm)
  );

  return [...nameMatches, ...cityMatches];
}

// Find courses near a location
export async function findNearestCourses(
  latitude: number,
  longitude: number,
  radiusMiles: number,
  excludePrivate = false
): Promise<CompactCourseModel[]> {
  // Load all states in parallel
  const allCourses = await Promise.all(
    AVAILABLE_STATES.map(state => loadStateData(state))
  ).then(states => states.flat());

  const radiusKm = radiusMiles * 1.60934;

  return allCourses
    .filter(course => {
      if (excludePrivate && course.t === 'private') {
        return false;
      }

      const distance = calculateDistance(
        latitude,
        longitude,
        course.g[0],
        course.g[1]
      );

      return distance <= radiusKm;
    })
    .sort((a, b) => {
      const distA = calculateDistance(
        latitude,
        longitude,
        a.g[0],
        a.g[1]
      );
      const distB = calculateDistance(
        latitude,
        longitude,
        b.g[0],
        b.g[1]
      );
      return distA - distB;
    });
}

export const filterByRadius = (
  courses: CompactCourseModel[],
  latitude: number,
  longitude: number,
  radiusMiles: number
): CompactCourseModel[] => {
  return courses.filter(course => {
    const distance = calculateDistance(
      latitude,
      longitude,
      course.g[0],
      course.g[1]
    );
    return distance <= radiusMiles;
  });
};
